import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Tag } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CalendarOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../../../layout/Layout";
import { fetchBlogById } from "../../../../redux/slice/blog/blogThunk";
import { baseUrl } from "../../../../services/api";

const BlogView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedBlog: blog, loading } = useSelector((state) => state.blog);

  useEffect(() => {
    if (id) {
      dispatch(fetchBlogById(id));
    }
  }, [dispatch, id]);

  if (loading || !blog) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900">
              {loading ? "Loading blog..." : "Blog not found"}
            </h3>
            {!loading && (
              <>
                <p className="text-gray-500 mt-1">
                  The blog post you're looking for doesn't exist.
                </p>
                <Button
                  type="primary"
                  onClick={() => navigate("/blog-details")}
                  className="mt-4"
                >
                  Back to Blogs
                </Button>
              </>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container my-4">
        <div className="mx-auto">
          {/* Top Button Bar */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/blog-details")}
              className="btn btn_contained_black fs_12 btn-sm text-white px-3"
            >
              Back
            </Button>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/blog-details/edit/${blog.id}`)}
              className="btn btn_contained_black fs_12 btn-sm text-white px-3"
            >
              Edit Blog
            </Button>
          </div>

          {/* Blog Container */}
          <div className="bg-white rounded shadow-sm overflow-hidden border">
            {blog.image && (
              <div style={{ height: "320px", overflow: "hidden" }}>
                <img
                  // src={blog.image}
                  src={baseUrl + blog.image}
                  alt={blog.title}
                  className="w-100 h-100"
                  style={{ objectFit: "cover" }}
                />
              </div>
            )}

            <div className="p-4 p-md-5">
              {/* Status + Author/Date */}
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <Tag
                  color={blog.status === "published" ? "green" : "orange"}
                  className="small"
                >
                  {blog?.status?.toUpperCase()}
                </Tag>
                <div className="d-flex text-muted small gap-3">
                  <div className="d-flex align-items-center gap-1">
                    <UserOutlined />
                    <span>{blog.author}</span>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <CalendarOutlined />
                    <span>
                      {format(new Date(blog.createdAt), "MMMM dd, yyyy")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Title */}
              <h1 className="h3 mb-4">{blog.title}</h1>

              {/* Tags */}
              <div className="mb-4 d-flex flex-wrap gap-2">
                {blog.tags.map((tag, index) => (
                  <Tag key={index} color="blue" className="px-2 py-1">
                    {tag}
                  </Tag>
                ))}
              </div>

              <hr className="my-4" />

              {/* Content */}
              <div
                className="lh-lg"
                style={{ fontSize: "16px", color: "#374151" }}
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />

              <hr className="my-4" />

              {/* Timestamps */}
              <div className="d-flex justify-content-between text-muted small">
                <div>
                  Created: {format(new Date(blog.createdAt), "MMMM dd, yyyy")}
                </div>
                <div>
                  Last updated:{" "}
                  {format(new Date(blog.updatedAt), "MMMM dd, yyyy")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BlogView;
